import { useMemo } from "react";
import type { ProjectManifest } from "@pix/shared";
import { lintProject, type LintIssue } from "../grid/lint";

interface Props {
  scripts: Record<string, string>;
  manifest: ProjectManifest;
  onJump: (file: string, line: number) => void;
}

export function LintPanel({ scripts, manifest, onJump }: Props) {
  const result = useMemo(
    () => lintProject(scripts, manifest),
    [scripts, manifest],
  );

  const counts = useMemo(() => {
    let err = 0, warn = 0, info = 0;
    for (const i of result.issues) {
      if (i.severity === "error") err++;
      else if (i.severity === "warning") warn++;
      else info++;
    }
    return { err, warn, info };
  }, [result.issues]);

  const groups = useMemo(() => {
    const g: Record<string, LintIssue[]> = {};
    for (const i of result.issues) (g[i.file] ??= []).push(i);
    for (const arr of Object.values(g)) arr.sort((a, b) => a.line - b.line);
    return g;
  }, [result.issues]);

  return (
    <div className="lint-panel">
      <div className="lint-header">
        <span className="lint-stat err">● {counts.err}</span>
        <span className="lint-stat warn">▲ {counts.warn}</span>
        <span className="lint-stat info">○ {counts.info}</span>
      </div>
      {result.issues.length === 0 ? (
        <div className="lint-empty">問題は見つかりませんでした 👍</div>
      ) : (
        <div className="lint-list">
          {Object.entries(groups).map(([file, list]) => (
            <div key={file} className="lint-file">
              <div className="lint-file-label">
                {file} <span className="lint-file-count">({list.length})</span>
              </div>
              {list.map((it, i) => (
                <button
                  key={i}
                  className={`lint-item lint-${it.severity}`}
                  onClick={() => onJump(it.file, it.line)}
                  title={`${file}:${it.line}`}
                >
                  <span className="lint-marker">
                    {it.severity === "error" ? "●" : it.severity === "warning" ? "▲" : "○"}
                  </span>
                  <span className="lint-line">L{it.line}</span>
                  <span className="lint-msg">{it.message}</span>
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
